import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * Cancel a shift with reason. Used by both facility and confirmed professional.
 * props: { open, onClose, shiftId, onDone }
 */
export default function CancelDialog({ open, onClose, shiftId, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please share a brief reason");
    setBusy(true);
    try {
      await api.post(`/shifts/${shiftId}/cancel`, { reason });
      try {
        const { track } = await import("@/lib/track");
        track("shift_cancelled", { shift_id: shiftId });
      } catch (err) {
        console.warn("track shift_cancelled failed:", err);
      }
      toast.success("Shift cancelled. Any escrow will be refunded.");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Cancel</div>
            <div className="font-display text-lg font-bold">Cancel this shift</div>
          </div>
          <button onClick={onClose} data-testid="cancel-close" className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-sm text-slate-600">Any escrow / UPI hold on this shift will be fully refunded. Cash-on-shift arrangements are simply voided. The other side will be notified.</div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Reason *</Label>
            <Textarea data-testid="cancel-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. Emergency change of plan · Duplicate booking" className="rounded-xl" />
          </div>
          <Button variant="destructive" onClick={submit} disabled={busy} className="w-full h-12 rounded-full" data-testid="cancel-submit">
            {busy ? "Cancelling…" : "Cancel shift"}
          </Button>
        </div>
      </div>
    </div>
  );
}
